import { useEffect, useId, useRef, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { Badge, Card, EmptyState, ErrorState, Spinner } from '@/components/ui';
import { formatDate, formatDateTime, formatKrw } from '@/lib/format';
import { HelpButton } from './HelpButton';
import {
  AI_REVIEW_OFF_TEXT,
  aiChangeLines,
  aiOtherNotes,
  aiProse,
  aiReviewHelp,
  aiStatusView,
  breadthLine,
  clusterName,
  companyRiskLine,
  componentsMeaning,
  componentsView,
  declineBreakdown,
  declineCauseLabel,
  evidenceChip,
  factGroups,
  flagText,
  infoCountText,
  infoLabel,
  interpretationKindLabel,
  lateNote,
  MARKET_GROUP_TITLE,
  materialTag,
  opinionView,
  recentLegLine,
  referenceLine,
  regimeLine,
  relevanceView,
  reportVersion,
  retryNote,
  reviewFocusLabel,
  reviewTitle,
  signalReason,
  sourceTime,
  tierLabel,
  v3Output,
  v4Output,
  verdictView,
  type FactGroup,
} from './aiReviewDisplay';
import { focusEpisodeId } from './aiReviewMark';
import type { AiClaim, AiEpisode, AiReviewAi, AiReviewOutput, AiReviewOutputV4, AiReviewRow, AiReviewSource } from './aiReviewTypes';
import { AI_REVIEW_DAYS, useAiReview } from './useAiReview';

/**
 * 추천 탭 하위 탭 "저점 신호 검토". (2026-10-17 · BACKLOG I-94 · docs/impl/153 23.4)
 *
 * 스윙 감시 종목이 1단선에 닿은 순간(국면)마다 카드 하나 — 그때 알 수 있던 숫자(사실 칸) · 코드 판정 · 공시 · 기사.
 * AI 칸(2단계 · 153 29.5) — 줄 상태가 DONE 이면 보고서, NO_SOURCE · REJECTED · BUDGET · FAILED · PENDING · NO_NEW_INFO 이면 이유 한 줄,
 *   1단계 줄(FACTS_ONLY)은 전처럼 "준비 중" 자리만 둔다.
 * 기록 하나의 읽는 순서(2026-10-18 · 153 35.5 · GPT [AIR-R3] 10 · ✏️ 판 3 38.3 B · 39.2 G) — ① 신호 이유
 *   ② 시장 · 섹터 · 종목 분해(장눌림 국면 줄 · 그 밑 최근 다리(판 3.1) · 코드 성분 · 숫자 · 시장 민감도 참고 폭 · 같은 주기 신호 수) ③ 종목의 섹터 대비 추가 하락
 *   ④ 자료 충분도(코드 값 + 셈) ⑤ AI 의견(실험적 · 작게 · 회사 고유 악재 한 블록) → 나머지 AI 칸 → 사실 칸 표 · 출처(재료).
 *   AI 의견은 맨 앞 큰 배지가 아니다 — 가치는 "섹터보다 더 빠진 폭을 지금 자료로 얼마나 받치나" 를 보이는 데 있다(원인 몫이라 부르지 않는다 · GPT [AIR-R4] 23).
 *   판 1 사실 칸(성분 · 국면 칸 없음)은 옛 한 칸 판정을 그대로 보인다.
 *
 * 🔴 모달이 아니라 카드 안 펼침이다 — 폰에서 모달 안 긴 표는 닫기가 어렵고 스크롤이 겹친다(메모리 mobile-first-ui-quality · 153 23.4)
 * 🔴 숫자 · 문턱을 문구에 박지 않는다 — 도움말은 서버 설정값으로 그린다(aiReviewHelp)
 * 🔴 매매와 무관하다 — 화면 머리에 늘 적는다(CLAUDE.md 1.1 · 1.3)
 */

/** 기간 — 최근 30일 안에 연 국면 + 아직 열린 국면(서버 기본값과 같다 · 스윙 감시 표 🔎 칩과 같은 값 · useAiReview) */
const DAYS = AI_REVIEW_DAYS;

export function AiReviewPanel() {
  const query = useAiReview(DAYS);
  const config = query.data?.config ?? null;
  // AI 호출 스위치가 꺼졌다고 서버가 알려 줬을 때만 꺼짐 문장 — 설정을 아직 못 받았으면(null) 켜진 때 문장 그대로
  const aiOff = config?.llmEnabled === false;
  // 🔎 스윙(6개월) 감시 표 칩에서 왔으면(?ep=국면 id) 그 국면 카드를 펼치고 화면에 보이게 한다(2026-10-18) · 없거나 목록에 없으면 지금과 같다
  const [params] = useSearchParams();
  const focusId = focusEpisodeId(params.get('ep'), query.data?.episodes ?? []);

  return (
    <div className="flex flex-col gap-3">
      <div className="rounded-lg border border-line bg-surface-muted p-3 text-xs leading-relaxed text-fg-muted">
        <div className="flex items-start justify-between gap-2">
          <p>
            <strong className="text-fg">AI 의견은 검증 전이에요 · 계좌 매매와 무관해요 · 투자 판단은 본인 몫이에요.</strong>
            <br />
            스윙(6개월) 감시 종목이 1단선에 닿은 순간의 숫자와 공시 · 기사를 모아 둡니다. 최근 {DAYS}일 안에 연 국면과 아직 열린 국면을 보여요.{' '}
            {aiOff
              ? AI_REVIEW_OFF_TEXT
              : 'AI 2차 검토 의견은 준비되는 대로 붙어요(근거로 쓸 공시 · 기사가 없거나 상한에 닿은 기록은 이유 한 줄).'}{' '}
            근거 번호를 누르면 원문이 열려요.
          </p>
          <HelpButton help={aiReviewHelp(config)} />
        </div>
      </div>

      {query.isLoading && <Spinner />}

      {query.error && <ErrorState message={query.error.message} onRetry={() => void query.refetch()} />}

      {query.data && query.data.episodes.length === 0 && (
        <Card>
          <EmptyState
            title="아직 저점 신호가 없어요"
            description={`스윙(6개월) 감시 종목이 정규장 중 1단선에 닿으면 여기에 쌓여요. ${
              aiOff
                ? AI_REVIEW_OFF_TEXT
                : '기록이 쌓이면 공시 · 기사(보고서 직전 다시 받은 것 · 웹 검색 포함)를 모아 AI 2차 검토 의견을 차례로 붙여요(근거로 쓸 공시 · 기사가 하나도 없으면 붙이지 않아요)'
            }`}
          />
        </Card>
      )}

      {query.data?.episodes.map((episode) => (
        <EpisodeCard key={episode.id} episode={episode} focused={episode.id === focusId} aiOff={aiOff} />
      ))}
    </div>
  );
}

// ─────────────────────────────────────────────
// 국면 카드
// ─────────────────────────────────────────────

/**
 * @param focused 주소의 ep 가 이 국면이면 참 — 펼쳐 두고 화면에 스크롤해 보인다(감시 표 🔎 칩 · 2026-10-18)
 * @param aiOff   서버가 AI 호출이 꺼졌다고 알려 줬으면 참 — 대기(PENDING) 줄을 "만드는 중" 이 아니라 "다시 켜면 이어서" 로 그린다(2026-10-19 · V158)
 */
export function EpisodeCard({ episode, focused = false, aiOff = false }: { episode: AiEpisode; focused?: boolean; aiOff?: boolean }) {
  const [open, setOpen] = useState(focused);
  const bodyId = useId();
  // 펼침은 첫 상태(useState(focused))로 — 칩에서 오면 화면이 새로 그려져 카드도 새로 생긴다. 스크롤은 카드 머리로(Card 는 ref 를 받지 않는다)
  const headerRef = useRef<HTMLElement>(null);
  useEffect(() => {
    if (focused) {
      // scrollIntoView 가 없는 곳(jsdom 등)에서도 깨지지 않게 있을 때만 부른다
      headerRef.current?.scrollIntoView?.({ block: 'start', behavior: 'smooth' });
    }
  }, [focused]);
  const latest = episode.reviews[0];
  const verdict = verdictView(latest?.facts?.codeDeclineContext?.verdict);
  // 판 2 사실 칸은 성분을 머리 배지로 · 판 1 은 옛 판정
  const components = componentsView(latest?.facts);

  return (
    <Card className="min-w-0">
      <header ref={headerRef} className="flex scroll-mt-6 flex-wrap items-start justify-between gap-2">
        <div className="min-w-0">
          <p className="text-sm font-semibold text-fg">
            {episode.name}
            <span className="ml-1 tabular text-xs font-normal text-fg-subtle">{episode.symbol}</span>
          </p>
          <p className="text-xs text-fg-subtle">
            {episode.sector} · 신호 {formatDateTime(episode.openedAt)}
          </p>
        </div>
        <div className="flex shrink-0 flex-wrap items-center gap-1">
          {latest?.facts?.codeDeclineContext && (
            <Badge tone={components?.tone ?? verdict.tone}>{components?.label ?? verdict.label}</Badge>
          )}
          {episode.status === 'OPEN' ? (
            <Badge tone="info">열림</Badge>
          ) : (
            <Badge tone="neutral">닫힘{episode.closedDate ? ` · ${formatDate(episode.closedDate)}` : ''}</Badge>
          )}
        </div>
      </header>

      <dl className="mt-3 grid grid-cols-2 gap-2 text-xs sm:grid-cols-4">
        <Stat label="국면" value={episode.status === 'OPEN' ? `${episode.phaseDay}일째` : `${episode.phaseDay}일간`} />
        <Stat label="선 아래" value={`${episode.touches}번 · ${episode.daysBelow}일`} />
        <Stat label="깊은 단" value={`${episode.deepestLeg}단`} />
        <Stat label="고정 1단선" value={formatKrw(episode.frozenLine)} />
      </dl>

      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="mt-3 min-h-[36px] w-full rounded-lg bg-surface-hover px-3 text-sm text-fg-muted hover:text-fg"
      >
        {open ? '접기 ▴' : `자세히 보기 ▾ (기록 ${episode.reviews.length}개)`}
      </button>

      {open && (
        <div id={bodyId} className="mt-3 flex flex-col gap-4">
          {episode.reviews.map((review, index) => (
            <ReviewSection key={review.id} review={review} defaultOpen={index === 0} aiOff={aiOff} />
          ))}
        </div>
      )}
    </Card>
  );
}

function Stat({ label, value }: { label: string; value: string }) {
  return (
    <div className="min-w-0">
      <dt className="text-fg-subtle">{label}</dt>
      <dd className="tabular text-fg">{value}</dd>
    </div>
  );
}

// ─────────────────────────────────────────────
// 검토 줄 하나
// ─────────────────────────────────────────────

/**
 * 최신 줄은 펼쳐 두고 앞선 줄은 접어 둔다 — 국면이 길면 카드가 한없이 길어진다.
 * 펼친 몸통 순서(153 35.5) — ① 신호 이유 ② 분해(코드) → AI 칸(③ ~ ⑤ + 나머지) → 사실 칸 표 · 출처(재료).
 */
export function ReviewSection({ review, defaultOpen, aiOff = false }: { review: AiReviewRow; defaultOpen: boolean; aiOff?: boolean }) {
  const [open, setOpen] = useState(defaultOpen);
  const bodyId = useId();
  const decline = review.facts?.codeDeclineContext ?? null;
  const verdict = verdictView(decline?.verdict);
  const components = componentsView(review.facts);
  const regime = regimeLine(review.facts);
  const recentLeg = recentLegLine(review.facts);
  const reference = referenceLine(review.facts);
  const breadth = breadthLine(review.facts);
  const groups = factGroups(review.facts);
  // 판 v4 줄만 시장(거시) 묶음을 접어 둔다(153 43.4 "거시 숫자는 접힌 표") — 옛 판 · AI 없는 줄은 지금처럼 펼친 표
  const foldMacro = reportVersion(review.ai?.promptVersion) === 'v4';

  return (
    <section className="min-w-0 border-t border-line pt-3">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        aria-controls={bodyId}
        className="flex w-full flex-wrap items-baseline justify-between gap-1 text-left"
      >
        <span className="text-sm font-medium text-fg">{reviewTitle(review.kind, review.trigger)}</span>
        <span className="tabular text-xs text-fg-subtle">
          {formatDateTime(review.signalAt)} · {formatKrw(review.priceAtSignal)} {open ? '▴' : '▾'}
        </span>
      </button>

      {open && (
        <div id={bodyId} className="mt-2 flex flex-col gap-3">
          {/* ① 신호 이유 — 무엇이 이 기록을 열었나(사실 칸 머리) */}
          <div className="min-w-0 text-xs">
            <p className="font-semibold text-fg-muted">① 신호</p>
            <p className="mt-0.5 tabular leading-relaxed text-fg [overflow-wrap:anywhere]">{signalReason(review.trigger, review.facts)}</p>
          </div>

          {/* ② 시장 · 섹터 · 종목 분해 — 코드가 숫자로 가른 몫(정해 둔 문턱 · 통계 검증 전 · 원인 몫이 아니다)
               판 2 — 맨 위 장눌림 국면 줄(켜졌을 때만) → 성분 셋 → 숫자 → 참고 폭 → 같은 주기 신호 수(153 39.2 화면 ②) */}
          <div className="rounded-lg bg-surface-muted p-2 text-xs">
            <div className="flex flex-wrap items-center gap-2">
              <span className="font-semibold text-fg-muted">② 시장 · 섹터 · 종목 분해({components ? '코드 성분' : '코드 판정'})</span>
              <Badge tone={components?.tone ?? verdict.tone}>{components?.label ?? verdict.label}</Badge>
            </div>
            {regime && (
              <p className="mt-1 font-semibold leading-relaxed text-fg" data-testid="regime-line">
                {regime}
              </p>
            )}
            {/* 판 3.1(153 41.3 D) — 국면 줄 바로 밑 최근 다리(국면이 켜졌을 때만) */}
            {recentLeg && (
              <p className="mt-0.5 tabular leading-relaxed text-fg-muted" data-testid="recent-leg-line">
                {recentLeg}
              </p>
            )}
            <p className="mt-1 leading-relaxed text-fg-muted">{components ? componentsMeaning(review.facts) : verdict.meaning}</p>
            {decline && <p className="mt-1 tabular text-fg-subtle">{declineBreakdown(review.facts)}</p>}
            {reference && <p className="mt-1 leading-relaxed text-fg-subtle [overflow-wrap:anywhere]">{reference}</p>}
            {breadth && <p className="mt-1 text-fg-subtle">{breadth}</p>}
          </div>

          {/* ③ ~ ⑤ + 나머지 — AI 칸 */}
          <AiSection review={review} aiOff={aiOff} />

          {/* 재료 — 사실 칸 표 · 그 순간 보인 출처 */}
          {groups.length === 0 ? (
            <p className="text-xs text-fg-subtle">사실 칸을 계산하지 못했어요(그 순간 값이 비어 있었어요).</p>
          ) : (
            groups.map((group) => (
              <FactGroupView key={group.title} group={group} foldable={foldMacro && group.title === MARKET_GROUP_TITLE} />
            ))
          )}

          <SourceList sources={review.sources} />
        </div>
      )}
    </section>
  );
}

/**
 * 사실 칸 표 한 묶음. foldable 이면 처음엔 접어 두고 제목 버튼으로 편다(판 v4 줄의 시장(거시) 묶음 · 153 43.4) —
 * 거시 숫자는 회사 근거가 아니라 시장 상태라 보고서 읽기를 가리지 않게 뒤로 뺀다. 숫자는 지우지 않는다(펴면 그대로).
 */
function FactGroupView({ group, foldable }: { group: FactGroup; foldable: boolean }) {
  const [open, setOpen] = useState(!foldable);
  const bodyId = useId();
  const asOf = group.asOf && <span className="ml-1 font-normal text-fg-subtle">· {formatDate(group.asOf)} 기준</span>;
  return (
    <div className="min-w-0">
      {foldable ? (
        <button
          type="button"
          onClick={() => setOpen((v) => !v)}
          aria-expanded={open}
          aria-controls={bodyId}
          className="text-left text-xs font-semibold text-fg-muted"
        >
          {group.title}
          {asOf}
          <span className="ml-1 font-normal text-fg-subtle">
            · {group.rows.length}줄 {open ? '접기 ▴' : '펼치기 ▾'}
          </span>
        </button>
      ) : (
        <p className="text-xs font-semibold text-fg-muted">
          {group.title}
          {asOf}
        </p>
      )}
      {open && (
        <dl id={bodyId} className="mt-1 grid grid-cols-1 gap-x-3 gap-y-1 text-xs sm:grid-cols-[minmax(0,12rem)_1fr]">
          {group.rows.map((r) => (
            <div key={r.label} className="contents">
              <dt className="text-fg-subtle">{r.label}</dt>
              <dd className="tabular min-w-0 text-fg [overflow-wrap:anywhere]">{r.value}</dd>
            </div>
          ))}
        </dl>
      )}
    </div>
  );
}

// ─────────────────────────────────────────────
// AI 2차 검토(2단계 · 153 29.5)
// ─────────────────────────────────────────────

/** 줄 상태별 AI 칸 — 보고서(판 v4 · v3 모양을 promptVersion 으로 가름) · 이유 한 줄 · 1단계 "준비 중" */
function AiSection({ review, aiOff }: { review: AiReviewRow; aiOff: boolean }) {
  const view = aiStatusView(review.status, review.ai, aiOff);
  if (view.kind === 'none') {
    return (
      <div className="rounded-lg border border-dashed border-line p-3 text-center text-xs text-fg-subtle">
        AI 의견 없음 — 이 기록 때는 AI 호출이 꺼져 있었어요
      </div>
    );
  }
  // 🔴 판으로 가른다(153 43.4) — air-v4 이상 줄은 새 모양 · 옛 판 줄은 옛 모양 그대로(소급해 새 모양으로 그리지 않는다)
  const outputV4 = v4Output(review.ai);
  const outputV3 = outputV4 ? null : v3Output(review.ai);
  if (view.kind === 'reason' || !review.ai || (!outputV4 && !outputV3)) {
    return (
      <div className="rounded-lg border border-dashed border-line p-3 text-xs leading-relaxed text-fg-subtle">{view.text}</div>
    );
  }
  // 판 3.1(153 41.3 C · D) — 검사에 한 번 걸려 같은 자료로 다시 써 통과한 보고서면 보고서 아래 작은 글씨 한 줄
  const retry = retryNote(review.status, review.ai);
  // 153 41.8 ① — 하루 상한에 밀려 다음 거래일에 쓴 보고서면 보고서 위에 작은 "늦은 보고서" 표시 + 한 줄(읽기 전에 알게)
  const late = lateNote(review.status, review.ai);
  return (
    <>
      {late && (
        <p className="text-[11px] leading-snug text-fg-subtle" data-testid="late-note">
          <Badge tone="neutral">늦은 보고서</Badge> <span>{late}</span>
        </p>
      )}
      {outputV4 ? (
        <AiReportV4 ai={review.ai} output={outputV4} />
      ) : (
        outputV3 && <AiReport ai={review.ai} output={outputV3} />
      )}
      {retry && (
        <p className="text-[11px] leading-snug text-fg-subtle" data-testid="retry-note">
          {retry}
        </p>
      )}
    </>
  );
}

/**
 * DONE 보고서(형식 v3 · 153 35.5 · 38.3) — ③ 종목의 섹터 대비 추가 하락 ④ 자료 충분도(코드 값 + 셈) ⑤ AI 의견(실험적 · 작게) → 나머지.
 * 🔴 ⑤ 의 회사 고유 악재는 한 블록 한 줄이다 — "못 찾음" 과 "악재가 없다는 뜻이 아니에요" 를 떼어 놓지 않는다(GPT [AIR-R3] 9).
 * 위험 묶음은 수준 배지 없이 이름 · 해석 · 관측 근거만(38.3 D).
 */
export function AiReport({ ai, output }: { ai: AiReviewAi; output: AiReviewOutput }) {
  const opinion = opinionView(ai.opinion);
  const changes = aiChangeLines(ai);
  const notes = aiOtherNotes(ai);
  const infoCount = infoCountText(ai);

  return (
    <div className="min-w-0 text-xs">
      {/* ③ 종목의 섹터 대비 추가 하락 — 원인 몫이 아니다(38.3 B) */}
      <div className="min-w-0">
        <p className="font-semibold text-fg-muted">③ 종목의 섹터 대비 추가 하락</p>
        {output.relativeExtraDecline.length === 0 ? (
          <p className="mt-0.5 text-fg-subtle">AI 가 따로 적은 "섹터 대비 추가 하락" 이 없어요.</p>
        ) : (
          <ClaimList claims={output.relativeExtraDecline} ai={ai} keyPrefix="relative-extra" />
        )}
      </div>

      {/* ④ 자료 충분도 — 코드가 센 값(AI 가 정하지 않는다 · 35.1) */}
      <div className="mt-3 min-w-0">
        <p className="font-semibold text-fg-muted">
          ④ 자료 충분도 <span className="font-normal text-fg">{infoLabel(ai.infoSufficiency)}</span>
        </p>
        <p className="mt-0.5 leading-relaxed text-fg-subtle">
          {infoCount ? `${infoCount} — ` : ''}코드가 센 자료 양이에요(AI 의 확신이 아니에요).
        </p>
      </div>

      {/* ⑤ AI 의견(실험적) — 작은 배지 · 맨 앞 큰 배지 아님 */}
      <div className="mt-3 min-w-0 rounded-lg border border-line p-2">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-fg-muted">⑤ AI 의견(실험적)</span>
          <Badge tone={opinion.tone}>{opinion.label}</Badge>
        </div>
        <p className="mt-1 text-fg-subtle">검증 전이에요 · 계좌 매매와 무관해요 · 투자 판단은 본인 몫이에요. {opinion.meaning}</p>
        <div className="mt-1 leading-relaxed text-fg" data-testid="company-risk-line">
          {companyRiskLine(ai.companyRisk)}
          <EvidenceChips ids={output.companyRiskEvidenceIds} ai={ai} />
        </div>
        {changes.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1 rounded-lg bg-surface-muted p-2 text-fg-muted">
            {changes.map((line) => (
              <li key={line}>{line}</li>
            ))}
          </ul>
        )}
        <div className="mt-1 leading-relaxed text-fg-muted">
          <span className="text-fg">관찰된 하락 맥락 {declineCauseLabel(output.llmDeclineCause)}</span>
          <span className="text-fg-subtle"> (② 분해와 다를 수 있어요)</span>
          <EvidenceChips ids={output.declineCauseEvidenceIds} ai={ai} />
        </div>
        <ClaimBlock title="요약" claims={[output.summary]} ai={ai} />
      </div>

      <ClaimBlock title="확인된 사실" claims={output.confirmedFacts} ai={ai} />
      <ClaimBlock title="시장 · 섹터 동반 움직임" claims={output.coMovement} ai={ai} />
      <ClaimBlock title="추가 확인할 것" claims={output.toCheck} ai={ai} />
      <ClaimBlock title="거시 관측(값만)" claims={output.macroObservations} ai={ai} />

      {output.riskClusters.length > 0 && (
        <div className="mt-3 min-w-0">
          <p className="font-semibold text-fg-muted">위험 묶음</p>
          <ul className="mt-1 flex flex-col gap-2">
            {output.riskClusters.map((c, i) => (
              <li key={`${c.cluster}-${i}`} className="min-w-0">
                <p className="text-fg">{clusterName(c.cluster)}</p>
                <p className="mt-0.5 leading-relaxed text-fg-muted [overflow-wrap:anywhere]">해석: {aiProse(c.interpretation)}</p>
                <EvidenceChips ids={c.observedFacts} ai={ai} label="관측" />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 반대 논거 — 눈에 띄게(153 8.2 · GPT 10절) · 이 보고서가 내린 판단의 가장 약한 고리(35.2) */}
      <div className="mt-3 rounded-lg border-2 border-warn bg-surface-muted p-2">
        <p className="font-semibold text-fg">반대 논거 — 이 판단이 틀릴 가장 강한 이유</p>
        <p className="mt-1 leading-relaxed text-fg [overflow-wrap:anywhere]">{aiProse(output.counterArgument.claim)}</p>
        <EvidenceChips ids={output.counterArgument.evidenceIds} ai={ai} />
      </div>

      <ClaimBlock title="이 판단이 무효가 되는 조건" claims={output.invalidateIf} ai={ai} />

      {output.upcomingEvents.length > 0 && (
        <div className="mt-3 min-w-0">
          <p className="font-semibold text-fg-muted">다가오는 일정(공시 제목에서)</p>
          <ul className="mt-1 flex flex-col gap-1">
            {output.upcomingEvents.map((e, i) => (
              <li key={`${e.event}-${i}`} className="leading-relaxed text-fg [overflow-wrap:anywhere]">
                {aiProse(e.event)}
                {e.date ? <span className="text-fg-subtle"> · {e.date}</span> : null}
                <EvidenceChips ids={e.evidenceIds} ai={ai} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {output.dataGaps.length > 0 && (
        <div className="mt-3 min-w-0">
          <p className="font-semibold text-fg-muted">모자랐던 자료</p>
          <ul className="mt-1 list-disc pl-4 text-fg-muted">
            {output.dataGaps.map((g) => (
              <li key={g} className="[overflow-wrap:anywhere]">{aiProse(g)}</li>
            ))}
          </ul>
        </div>
      )}

      {(ai.flags.length > 0 || notes.length > 0) && (
        <ul className="mt-3 flex flex-col gap-1 text-fg-subtle">
          {ai.flags.map((f) => (
            <li key={f}>중복 의심 — {flagText(f)}</li>
          ))}
          {notes.map((n) => (
            <li key={n}>저장 전 검사 — {n}</li>
          ))}
        </ul>
      )}

      <p className="mt-3 tabular text-fg-subtle">
        분석 기준 {ai.evidenceCutoffAt ? formatDateTime(ai.evidenceCutoffAt) : '-'} · {ai.model ?? '-'} · 프롬프트 {ai.promptVersion ?? '-'}
      </p>
    </div>
  );
}

/**
 * DONE 보고서(판 v4 · air-v4 이상 · 153 43.4 화면 순서) — ④ 자료 충분도(코드 · 지금 자리) → ⑤ 세 줄 요지(위치 · 회사 이야기 · 먼저 확인할 것)
 *   + 검토 초점 칩 + 회사 고유 악재 한 줄 → 근거와 해석(관찰 · 의미(굵게) · 한계(작게)) → 사건(있으면 · 출처 수) → 다음 확인 → 반대 논거 → 자료 공백 → 인용 출처(관련도 칩).
 * 🔴 의견 배지(매수 검토 · 관망 · 피함)는 없다 — v4 는 의견 칸을 없애고 검토 초점으로 바꿨다(43.3 · 43.5 ②). 숫자는 코드 칸(① ② ④ · 사실 칸 표)에 둔다.
 * ③ 종목의 섹터 대비 추가 하락은 v4 출력에 없다(빼는 칸) — ② 분해의 코드 숫자가 그 자리다.
 */
export function AiReportV4({ ai, output }: { ai: AiReviewAi; output: AiReviewOutputV4 }) {
  const changes = aiChangeLines(ai);
  const notes = aiOtherNotes(ai);
  const infoCount = infoCountText(ai);
  const { threeLines, reviewFocus } = output;

  return (
    <div className="min-w-0 text-xs" data-testid="ai-report-v4">
      {/* ④ 자료 충분도 — 코드가 센 값(지금 자리 · AI 가 정하지 않는다 · 35.1) */}
      <div className="min-w-0">
        <p className="font-semibold text-fg-muted">
          ④ 자료 충분도 <span className="font-normal text-fg">{infoLabel(ai.infoSufficiency)}</span>
        </p>
        <p className="mt-0.5 leading-relaxed text-fg-subtle">
          {infoCount ? `${infoCount} — ` : ''}코드가 센 자료 양이에요(AI 의 확신이 아니에요).
        </p>
      </div>

      {/* ⑤ 세 줄 요지 — 30초 안에 "왜 다시 볼지 · 무엇을 확인할지"(43.5 일곱째 평가) */}
      <div className="mt-3 min-w-0 rounded-lg border border-line p-2" data-testid="three-lines">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-semibold text-fg-muted">⑤ AI 세 줄 요지(실험적)</span>
          <Badge tone="info">검토 초점 · {reviewFocusLabel(reviewFocus.focus)}</Badge>
        </div>
        <p className="mt-1 text-fg-subtle">검증 전이에요 · 계좌 매매와 무관해요 · 투자 판단은 본인 몫이에요.</p>
        <div className="mt-1 flex flex-col gap-1.5">
          <ThreeLine label="위치" claim={threeLines.position} ai={ai} />
          <ThreeLine label="회사 이야기" claim={threeLines.companyStory} ai={ai} />
          <ThreeLine label="먼저 확인할 것" claim={threeLines.firstCheck} ai={ai} />
        </div>
        <div className="mt-1.5 min-w-0 leading-relaxed text-fg-muted" data-testid="review-focus-line">
          <p className="break-keep [overflow-wrap:anywhere]">검토 초점 — {aiProse(reviewFocus.line)}</p>
          <EvidenceChips ids={reviewFocus.evidenceIds} ai={ai} />
        </div>
        <div className="mt-1 leading-relaxed text-fg" data-testid="company-risk-line">
          {companyRiskLine(ai.companyRisk)}
          <EvidenceChips ids={output.companyRiskEvidenceIds} ai={ai} />
        </div>
        {changes.length > 0 && (
          <ul className="mt-1 flex flex-col gap-1 rounded-lg bg-surface-muted p-2 text-fg-muted">
            {changes.map((line) => (
              <li key={line} className="break-keep [overflow-wrap:anywhere]">{line}</li>
            ))}
          </ul>
        )}
      </div>

      {/* 근거와 해석 — 관찰(본 것) · 의미(그래서 무엇 · 굵게) · 한계(어디까지만 · 작게) */}
      {output.interpretations.length > 0 && (
        <div className="mt-3 min-w-0">
          <p className="font-semibold text-fg-muted">근거와 해석</p>
          <ul className="mt-1 flex flex-col gap-2">
            {output.interpretations.map((it, i) => (
              <li key={`interpretation-${i}`} className="min-w-0 rounded-lg bg-surface-muted p-2" data-testid="interpretation">
                <Badge tone="neutral">{interpretationKindLabel(it.kind)}</Badge>
                <p className="mt-1 leading-relaxed text-fg break-keep [overflow-wrap:anywhere]">
                  <span className="text-fg-subtle">관찰 </span>
                  {aiProse(it.observation)}
                </p>
                <p className="mt-0.5 leading-relaxed text-fg break-keep [overflow-wrap:anywhere]">
                  <span className="text-fg-subtle">의미 </span>
                  <strong className="font-semibold" data-testid="interpretation-meaning">
                    {aiProse(it.meaning)}
                  </strong>
                </p>
                <p className="mt-0.5 text-[11px] leading-snug text-fg-subtle break-keep [overflow-wrap:anywhere]" data-testid="interpretation-limit">
                  한계 {aiProse(it.limit)}
                </p>
                <EvidenceChips ids={it.evidenceIds} ai={ai} />
              </li>
            ))}
          </ul>
        </div>
      )}

      {/* 사건 — 같은 사건 여러 기사는 한 사건(43.3) · 있을 때만 */}
      {output.events.length > 0 && (
        <div className="mt-3 min-w-0">
          <p className="font-semibold text-fg-muted">사건</p>
          <ul className="mt-1 flex flex-col gap-1">
            {output.events.map((e, i) => (
              <li key={`${e.name}-${i}`} className="leading-relaxed text-fg break-keep [overflow-wrap:anywhere]" data-testid="event">
                {aiProse(e.name)}
                <span className="text-fg-subtle"> · 출처 {e.sourceIds.length}</span>
              </li>
            ))}
          </ul>
        </div>
      )}

      <ClaimBlock title="다음 확인" claims={output.nextChecks} ai={ai} />

      {/* 반대 논거 — 눈에 띄게(153 8.2) · 이 요지의 가장 약한 고리 */}
      <div className="mt-3 rounded-lg border-2 border-warn bg-surface-muted p-2">
        <p className="font-semibold text-fg">반대 논거 — 이 요지가 틀릴 가장 강한 이유</p>
        <p className="mt-1 leading-relaxed text-fg break-keep [overflow-wrap:anywhere]">{aiProse(output.counterArgument.claim)}</p>
        <EvidenceChips ids={output.counterArgument.evidenceIds} ai={ai} />
      </div>

      {output.dataGaps.length > 0 && (
        <div className="mt-3 min-w-0">
          <p className="font-semibold text-fg-muted">자료 공백</p>
          <ul className="mt-1 list-disc pl-4 text-fg-muted">
            {output.dataGaps.map((g) => (
              <li key={g} className="break-keep [overflow-wrap:anywhere]">{aiProse(g)}</li>
            ))}
          </ul>
        </div>
      )}

      <CitedSources output={output} ai={ai} />

      {(ai.flags.length > 0 || notes.length > 0) && (
        <ul className="mt-3 flex flex-col gap-1 text-fg-subtle">
          {ai.flags.map((f) => (
            <li key={f}>저장 전 검사 — {flagText(f)}</li>
          ))}
          {notes.map((n) => (
            <li key={n}>저장 전 검사 — {n}</li>
          ))}
        </ul>
      )}

      <p className="mt-3 tabular text-fg-subtle">
        분석 기준 {ai.evidenceCutoffAt ? formatDateTime(ai.evidenceCutoffAt) : '-'} · {ai.model ?? '-'} · 프롬프트 {ai.promptVersion ?? '-'}
      </p>
    </div>
  );
}

/** 세 줄 요지 한 줄 — 이름 · 문장 · 근거 칩 */
function ThreeLine({ label, claim, ai }: { label: string; claim: AiClaim; ai: AiReviewAi }) {
  return (
    <div className="min-w-0" data-testid="three-line">
      <p className="leading-relaxed text-fg break-keep [overflow-wrap:anywhere]">
        <span className="font-semibold text-fg-muted">{label} </span>
        {aiProse(claim.claim)}
      </p>
      <EvidenceChips ids={claim.evidenceIds} ai={ai} />
    </div>
  );
}

/** 인용 출처(판 v4) — AI 가 인용한 S 마다 관련도 칩(회사가 주인공 · 회사에 직접 · 지나가는 언급) + 원문 링크 칩 + 제목 */
function CitedSources({ output, ai }: { output: AiReviewOutputV4; ai: AiReviewAi }) {
  if (output.sourceRelevance.length === 0) {
    return null;
  }
  return (
    <div className="mt-3 min-w-0">
      <p className="font-semibold text-fg-muted">인용 출처</p>
      <ul className="mt-1 flex flex-col gap-1.5">
        {output.sourceRelevance.map((s) => {
          const relevance = relevanceView(s.relevance);
          const source = Object.hasOwn(ai.sourceMap, s.sourceId) ? ai.sourceMap[s.sourceId] : undefined;
          return (
            <li key={s.sourceId} className="min-w-0" data-testid="cited-source">
              <div className="flex flex-wrap items-center gap-1">
                <Badge tone={relevance.tone}>{relevance.label}</Badge>
                <EvidenceChipView id={s.sourceId} ai={ai} />
              </div>
              {source?.title && <p className="mt-0.5 leading-snug text-fg-muted [overflow-wrap:anywhere]">{source.title}</p>}
            </li>
          );
        })}
      </ul>
    </div>
  );
}

/** 주장 목록 — 문장 + 근거 칩. 비었으면 그리지 않는다 */
function ClaimBlock({ title, claims, ai }: { title: string; claims: AiClaim[]; ai: AiReviewAi }) {
  if (claims.length === 0) {
    return null;
  }
  return (
    <div className="mt-3 min-w-0">
      <p className="font-semibold text-fg-muted">{title}</p>
      <ClaimList claims={claims} ai={ai} keyPrefix={title} />
    </div>
  );
}

/** 주장 목록 몸통(제목 없이) — 문장 + 근거 칩 */
function ClaimList({ claims, ai, keyPrefix }: { claims: AiClaim[]; ai: AiReviewAi; keyPrefix: string }) {
  return (
    <ul className="mt-1 flex flex-col gap-1.5">
      {claims.map((c, i) => (
        <li key={`${keyPrefix}-${i}`} className="min-w-0">
          <p className="leading-relaxed text-fg [overflow-wrap:anywhere]">{aiProse(c.claim)}</p>
          <EvidenceChips ids={c.evidenceIds} ai={ai} />
        </li>
      ))}
    </ul>
  );
}

/** 근거 번호 칩 — S 는 원문 링크(새 창) · F 는 이름과 값 */
function EvidenceChips({ ids, ai, label }: { ids: string[]; ai: AiReviewAi; label?: string }) {
  if (ids.length === 0) {
    return null;
  }
  return (
    <div className="mt-1 flex flex-wrap items-center gap-1">
      {label && <span className="text-fg-subtle">{label}</span>}
      {ids.map((id) => (
        <EvidenceChipView key={id} id={id} ai={ai} />
      ))}
    </div>
  );
}

/** 근거 번호 칩 하나(EvidenceChips · 판 v4 인용 출처가 같이 쓴다) */
function EvidenceChipView({ id, ai }: { id: string; ai: AiReviewAi }) {
  const chip = evidenceChip(id, ai);
  const className = 'inline-block max-w-full rounded border border-line px-1.5 py-0.5 text-[11px] leading-snug [overflow-wrap:anywhere]';
  return chip.href ? (
    <a
      href={chip.href}
      target="_blank"
      rel="noopener noreferrer"
      title={chip.title}
      className={`${className} text-fg underline-offset-2 hover:underline`}
    >
      {chip.text}
    </a>
  ) : (
    <span title={chip.title} className={`${className} text-fg-muted`}>
      {chip.text}
    </span>
  );
}

// ─────────────────────────────────────────────
// 출처
// ─────────────────────────────────────────────

function SourceList({ sources }: { sources: AiReviewSource[] }) {
  if (sources.length === 0) {
    return <p className="text-xs text-fg-subtle">그 순간까지 받아 둔 공시 · 기사가 없어요.</p>;
  }
  return (
    <div className="min-w-0">
      <p className="text-xs font-semibold text-fg-muted">그 순간 보인 공시 · 기사 ({sources.length}개)</p>
      <ul className="mt-1 flex flex-col gap-2">
        {sources.map((s) => {
          const material = materialTag(s.tags);
          return (
            <li key={s.id} className="min-w-0 text-xs">
              <div className="flex flex-wrap items-center gap-1 text-fg-subtle">
                <Badge tone={s.tier === '1A' ? 'success' : 'neutral'}>{tierLabel(s.tier)}</Badge>
                {material && <Badge tone="warning">중요 · {material}</Badge>}
                <span>{s.publisher ?? '-'}</span>
                <span className="tabular">· {sourceTime(s)}</span>
              </div>
              <a
                href={s.url}
                target="_blank"
                rel="noopener noreferrer"
                className="mt-0.5 block text-fg underline-offset-2 hover:underline [overflow-wrap:anywhere]"
              >
                {s.title ?? s.url}
              </a>
            </li>
          );
        })}
      </ul>
    </div>
  );
}
